import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  PLATFORM_ID,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { TimeUtils } from '@freelancer/time-utils';
// docs say to use the polyfill as a ponyfill
import ResizeObserver from 'resize-observer-polyfill';
import * as Rx from 'rxjs';
import { StickyService } from './sticky.service';

/**
 * ALWAYS
 * - Element is always sticky and visible at all times
 *
 * ONSCROLL
 * - Element will only be sticky once it intersects with the device's viewport
 *
 * APPEAR_AFTER_SCROLL
 * - Element is always sticky but hidden by default, it will only
 *   be visible once the it intersects with the device's viewport
 */
export enum StickyBehaviour {
  ALWAYS = 'always',
  ONSCROLL = 'onscroll',
  APPEAR_AFTER_SCROLL = 'appear_after_scroll',
}

export enum StickyPosition {
  TOP = 'top',
  BOTTOM = 'bottom',
}

@Directive({
  selector: `
    [flSticky],
  `,
})
export class StickyDirective implements OnChanges, AfterViewInit, OnDestroy {
  @Input() flSticky = true;
  @Input() flStickyOffset = 0;
  @Input() flStickyBehaviour = StickyBehaviour.ONSCROLL;
  @Input() flStickyPosition = StickyPosition.TOP;
  @Input() flStickyOrder: number;

  /**
   * Warning
   * This makes the element not included in the stacking and gonna
   * overlap with other sticky elements
   */
  @Input() flStickyStatic = false;

  @Output() activated = new EventEmitter<boolean>();

  private get isStickyTop() {
    return this.flStickyPosition === StickyPosition.TOP;
  }

  private get isAlwaysSticky() {
    return this.flStickyBehaviour === StickyBehaviour.ALWAYS;
  }

  private get isAppearAfterScroll() {
    return this.flStickyBehaviour === StickyBehaviour.APPEAR_AFTER_SCROLL;
  }

  private get element(): HTMLElement {
    return this.elementRef.nativeElement;
  }

  private get markerPosition() {
    return window.scrollY + this.marker.getBoundingClientRect().top;
  }

  private isSticky = false;
  private offset = this.flStickyOffset;
  private stackHeightSubscription?: Rx.Subscription;
  private intersectionObserver: IntersectionObserver;
  private resizeObserver: ResizeObserver;
  private placeholderResizeObserver: ResizeObserver;
  private marker = this.renderer.createElement('div') as HTMLDivElement;
  private placeholder = this.renderer.createElement('div') as HTMLDivElement;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private stickyService: StickyService,
    private timeUtils: TimeUtils,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    // Don't set up other functionalities of the
    // directive in the server as things like
    // getBoundingClientRect will break SSR
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if ('flSticky' in changes && this.elementRef && this.flSticky) {
      this.initializeSticky();
    } else {
      this.deactivate();
      this.destroyListeners();
    }
  }

  ngAfterViewInit() {
    // Set styling for APPEAR_AFTER_SCROLL since it should not
    // take up space and be hidden by default
    if (this.flSticky && this.isAppearAfterScroll) {
      this.renderer.setStyle(this.element, 'position', 'fixed');
      this.hideStickyElement();
    }

    // Don't set up other functionalities of the
    // directive in the server as things like
    // getBoundingClientRect will break SSR
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (this.flSticky) {
      this.timeUtils.setTimeout(() => {
        this.initializeSticky();
      });
    }
  }

  initializeSticky() {
    this.offset = this.getOffset();

    this.createMarker();

    if (this.isElementVisible()) {
      this.toggleStickyState();
    }

    if (typeof this.resizeObserver === 'undefined') {
      this.resizeObserver = new ResizeObserver(
        (entries: ResizeObserverEntry[]) => {
          requestAnimationFrame(() => {
            entries.forEach(() => {
              if (!this.isElementVisible() && this.isSticky) {
                this.deactivate();
              }

              if (this.isElementVisible()) {
                this.detectStackHeightChanges();
                this.toggleStickyState();
              }

              if (this.isSticky) {
                this.updateStickyPosition();
                this.updatePlaceholderHeight();
              }
            });
          });
        },
      );

      this.resizeObserver.observe(this.element);
    }

    this.detectStackHeightChanges();
  }

  detectStackHeightChanges() {
    if (
      typeof this.stackHeightSubscription === 'undefined' &&
      !this.flStickyStatic &&
      this.isElementVisible()
    ) {
      this.stackHeightSubscription = this.stickyService
        .stackHeightChange(this.isStickyTop)
        .subscribe(() => {
          this.offset = this.getOffset();
          this.updateMarkerPosition();

          if (this.isSticky) {
            this.updateStickyPosition();
          }
        });
    }
  }

  /**
   * Toggle Sticky State
   * Activates instantly for the ff:
   * 1. isAlwaysSticky
   * 2. isAppearAfterScroll should always be sticky so that it doesn't take up space
   * 3. Marker's position is already off the screen, this is for
   * cases when the page is already scrolled way past the marker
   *
   * IntersectionObserver is only used for `ONSCROLL` and `APPEAR_AFTER_SCROLL`
   */
  toggleStickyState() {
    if (
      this.isAlwaysSticky ||
      this.isAppearAfterScroll ||
      this.markerPosition <= 0
    ) {
      this.activate();
    }

    if (
      typeof this.intersectionObserver === 'undefined' &&
      !this.isAlwaysSticky
    ) {
      this.intersectionObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (this.isAppearAfterScroll) {
            this.toggleVisibility(entry);
            return;
          }

          if (this.isStickyTop) {
            this.toggleStickyTop(entry);
          } else {
            this.toggleStickyBottom(entry);
          }
        });
      });
      this.intersectionObserver.observe(this.marker);
    }
  }

  toggleVisibility(entry: IntersectionObserverEntry) {
    if (!entry.isIntersecting && this.marker.getBoundingClientRect().top <= 0) {
      this.showStickyElement();
    } else {
      this.hideStickyElement();
    }
  }

  showStickyElement() {
    this.renderer.removeStyle(this.element, 'pointer-events');
    this.renderer.removeStyle(this.element, 'visibility');

    this.activated.emit(true);
    this.addToStack();
  }

  hideStickyElement() {
    this.renderer.setStyle(this.element, 'pointer-events', 'none');
    this.renderer.setStyle(this.element, 'visibility', 'hidden');

    this.activated.emit(false);
    this.removeFromStack();
  }

  toggleStickyTop(entry: IntersectionObserverEntry) {
    if (!entry.isIntersecting && this.marker.getBoundingClientRect().top <= 0) {
      this.activate();
    } else {
      this.deactivate();
    }
  }

  toggleStickyBottom(entry: IntersectionObserverEntry) {
    if (entry.isIntersecting) {
      this.activate();
    } else if (this.marker.getBoundingClientRect().top > 0) {
      this.deactivate();
    }
  }

  activate() {
    if (!this.isSticky) {
      const { width } = this.element.getBoundingClientRect();

      // Don't create a placeholder for APPEAR_AFTER_SCROLL
      // as the element is hidden initially and this will create awkward space
      if (!this.isAppearAfterScroll) {
        this.createPlaceholder();
      }

      this.updateStickyPosition();

      this.renderer.addClass(this.element, 'IsSticky');
      this.renderer.setStyle(this.element, 'position', 'fixed');
      this.renderer.setStyle(this.element, 'z-index', '1000');
      this.renderer.setStyle(this.element, 'width', `${width}px`);

      // Adjusts scroll position in UI tests to prevent sticky header from
      // obscuring targeted element
      if (this.isStickyTop) {
        this.renderer.setAttribute(
          this.element,
          'data-testing',
          'sticky-header',
        );
      }

      this.isSticky = true;
      this.activated.emit(true);
      this.addToStack();
    }
  }

  deactivate() {
    if (this.isSticky) {
      this.isSticky = false;

      // We don't create a placeholder for APPEAR_AFTER_SCROLL
      if (!this.isAppearAfterScroll) {
        this.destoryPlaceholder();
      }

      this.renderer.removeStyle(this.element, 'position');
      this.renderer.removeStyle(this.element, this.flStickyPosition);
      this.renderer.removeStyle(this.element, 'z-index');
      this.renderer.removeStyle(this.element, 'width');
      this.renderer.removeClass(this.element, 'IsSticky');

      if (this.isStickyTop) {
        this.renderer.removeAttribute(this.element, 'data-testing');
      }

      this.activated.emit(false);
      this.removeFromStack();
    }
  }

  private addToStack() {
    if (!this.flStickyStatic) {
      this.stickyService.add(
        this.element,
        this.isStickyTop,
        this.flStickyOrder,
      );
    }
  }

  private removeFromStack() {
    if (!this.flStickyStatic) {
      this.stickyService.remove(this.element, this.isStickyTop);
    }
  }

  private getOffset() {
    return !this.flStickyStatic
      ? this.flStickyOffset +
          this.stickyService.getOffset(this.element, this.isStickyTop)
      : this.flStickyOffset;
  }

  private updateStickyPosition() {
    this.renderer.setStyle(
      this.element,
      this.flStickyPosition,
      `${this.offset}px`,
    );
  }

  /**
   * Placeholder is created to avoid jump when component becomes sticky
   * and serves as the width basis when user resizes the screen
   */
  private createPlaceholder() {
    this.renderer.setStyle(this.placeholder, 'display', 'block');
    this.updatePlaceholderHeight();

    if (this.isAlwaysSticky || this.isStickyTop) {
      this.renderer.appendChild(this.element.parentNode, this.placeholder);
    }

    if (!this.isStickyTop && !this.isAlwaysSticky) {
      this.renderer.insertBefore(
        this.element.parentNode,
        this.placeholder,
        this.marker,
      );
    }

    // Change sticky element width if placeholder width changes
    this.placeholderResizeObserver = new ResizeObserver(
      (entries: ResizeObserverEntry[]) => {
        requestAnimationFrame(() => {
          entries.forEach(() => {
            if (this.isSticky) {
              this.renderer.setStyle(
                this.element,
                'width',
                `${this.placeholder.offsetWidth}px`,
              );
            }
          });
        });
      },
    );

    this.placeholderResizeObserver.observe(this.placeholder);
  }

  private updatePlaceholderHeight() {
    this.renderer.setStyle(
      this.placeholder,
      'height',
      `${this.element.getBoundingClientRect().height}px`,
    );
  }

  private destoryPlaceholder() {
    // Set display to none as removing child sometimes takes time
    this.renderer.setStyle(this.placeholder, 'display', 'none');
    this.renderer.removeChild(this.element.parentNode, this.placeholder);

    if (this.placeholderResizeObserver) {
      this.placeholderResizeObserver.unobserve(this.placeholder);
    }
  }

  /**
   * Marker serves as the intersection point to decide
   * whether to activate or deactivate sticky state
   */
  private createMarker() {
    this.marker.style.position = `relative`;

    if (this.isStickyTop) {
      this.renderer.insertBefore(
        this.element.parentNode,
        this.marker,
        this.element,
      );
    } else {
      this.renderer.appendChild(this.element.parentNode, this.marker);
    }

    this.updateMarkerPosition();
  }

  private updateMarkerPosition() {
    this.renderer.setStyle(
      this.marker,
      this.flStickyPosition,
      `-${this.offset}px`,
    );
  }

  private destroyMarker() {
    this.renderer.removeChild(this.element.parentNode, this.marker);
  }

  private destroyListeners() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    if (this.stackHeightSubscription) {
      this.stackHeightSubscription.unsubscribe();
    }
  }

  private isElementVisible() {
    return this.element.offsetWidth > 0 || this.element.offsetHeight > 0;
  }

  ngOnDestroy() {
    this.destroyMarker();
    this.deactivate();
    this.destroyListeners();
  }
}
